import React, { createContext, useCallback, useContext, useState } from 'react';
import { noop } from 'lodash';

import type { PhoneCallExtraData } from '~/components/communications/types';
import useInterval from '~/components/hooks/useInterval';
import type { PhoneCallCommunication } from '~/server_shared/swagger/data-contracts';

import type { PartialCallConnectionProps } from '../types';

interface CommunicationCallContextCommunication extends PhoneCallCommunication {
  extra?: PhoneCallExtraData;
}

export interface CommunicationCallContextType {
  isMuted: boolean;
  isCallActive: boolean;
  callDurationSec: number;
  communication: CommunicationCallContextCommunication;
  callStatus?: string;
  callConnection?: PartialCallConnectionProps;
  currentlyClickedDigits: string;

  updateMute: (isMute: boolean) => void;
  updateCallConnection: (connection: PartialCallConnectionProps) => void;
  updateDisconnectOrCancel: (connection: PartialCallConnectionProps) => void;
  updateCommunication: (communication: PhoneCallCommunication) => void;
  handleSendDigit: (digit: string) => void;
  handleHangup: () => void;
}

export const CommunicationCallContext = createContext<CommunicationCallContextType>({
  isMuted: false,
  isCallActive: false,
  callDurationSec: 0,
  updateMute: noop,
  // @ts-expect-error Default empty object doesn't match CommunicationCallContextCommunication interface
  communication: {},
  updateCallConnection: noop,
  updateDisconnectOrCancel: noop,
  updateCommunication: noop,
  handleSendDigit: noop,
  handleHangup: noop,
  currentlyClickedDigits: '',
});

export const CommunicationCallProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [callDurationSec, setCallDurationSec] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  // @ts-expect-error Default empty object doesn't match CommunicationCallContextCommunication interface
  const [communication, setCommunication] = useState<PhoneCallCommunication>({});
  const [callStatus, setCallStatus] = useState<string>();
  const [callConnection, setCallConnection] = useState<PartialCallConnectionProps>();
  const [currentlyClickedDigits, setCurrentlyClickedDigits] = useState('');

  const isCallActive = callStatus === 'open';
  useInterval(() => setCallDurationSec(callDurationSec + 1), isCallActive ? 1000 : null);

  const updateCallConnection = useCallback((connection: PartialCallConnectionProps) => {
    setCallStatus(connection.status());
    setCallConnection(connection);
    setIsMuted(connection.isMuted());
  }, []);

  const updateDisconnectOrCancel = useCallback((connection: PartialCallConnectionProps) => {
    setCallStatus(connection.status());
    setCallConnection(undefined);
  }, []);

  const updateMute = useCallback(
    (shouldMute: boolean) => {
      callConnection && callConnection.mute(shouldMute);
      setIsMuted(callConnection?.isMuted() || shouldMute);
    },
    [callConnection]
  );

  const handleSendDigit = (digit: string) => {
    setCurrentlyClickedDigits((current) => current + digit);
    callConnection && callConnection.sendDigits(digit);
  };

  const handleHangup = () => {
    callConnection && callConnection.disconnect();
  };

  return (
    <CommunicationCallContext.Provider
      value={{
        isMuted,
        isCallActive,
        currentlyClickedDigits,
        callDurationSec,
        communication,
        callStatus,
        callConnection,
        updateMute,
        updateCallConnection,
        updateDisconnectOrCancel,
        handleSendDigit,
        handleHangup,
        updateCommunication: setCommunication,
      }}
    >
      {children}
    </CommunicationCallContext.Provider>
  );
};

export const useCommunicationCallContext = (): CommunicationCallContextType => {
  return useContext(CommunicationCallContext);
};
